import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import ReelBackDecorationController from "./ReelBackDecorationController";
import { safeInit } from "../../../utilities";
import {
  selectFreeSpinTransitionMidpoint,
  selectFreeSpinTriggered,
} from "../../../State/selectors";
import { selectShowFreeSpinOutro } from "../../../State/Slices/animationsSlice";
import { SPINE_CONFIG } from "../ReelBack/ReelBackController";
import { Settings } from "../../../settings";

/**
 * ReelBackDecoration Component
 * Plays event animations (event_in → event_idle) behind the reel frame
 * when the free spin scene transition reaches its midpoint.
 */
const ReelBackDecoration = ({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
}) => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const freeSpinTransitionMidpoint = useSelector(selectFreeSpinTransitionMidpoint);
  const freeSpinTriggered = useSelector(selectFreeSpinTriggered);
  const showFreeSpinOutro = useSelector(selectShowFreeSpinOutro);
  const prevShowOutroRef = useRef(false);

  // Initialize controller
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new ReelBackDecorationController(
          containerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
        );
      });
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resizeDecoration(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight, symbolWidth]);

  // Play event_in → event_idle when transition midpoint event fires during free spin trigger
  useEffect(() => {
    if (freeSpinTransitionMidpoint && freeSpinTriggered) {
      if (controllerRef.current) {
        controllerRef.current.playEventIn();
      }
    }
  }, [freeSpinTransitionMidpoint, freeSpinTriggered]);

  // Play event_out when outro dialog finishes (showFreeSpinOutro: true → false)
  useEffect(() => {
    if (prevShowOutroRef.current && !showFreeSpinOutro) {
      if (controllerRef.current) {
        controllerRef.current.playEventOut();
      }
    }
    prevShowOutroRef.current = showFreeSpinOutro;
  }, [showFreeSpinOutro]);

  return (
    <Container
      ref={containerRef}
      name="reel_back_decoration"
      y={-symbolHeight * Settings.SYMBOLS_VISIBLE * SPINE_CONFIG.yOffset}
      x={symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.xOffset}
      sortableChildren
      zIndex={0}
    />
  );
};

export default ReelBackDecoration;
